// Shared display helpers for the member-facing Meets surfaces.
// The API serialises enums as NUMBERS — the maps below mirror
// SLL.Api/Models/Entities/Meet.cs and MeetAttendance.cs exactly.
import {DateTime} from 'luxon';
import {looksLikeHtml, sanitiseTermsHtml} from '~/utils/sanitiseTermsHtml';
import {usePaymentsDisplay} from '~/composables/usePaymentsDisplay';
// MeetRecurrenceMode / MeetRecurrenceUnit / MeetMonthlyPattern come from the
// shared model in useMeetAdmin (the single exporter — a re-export here would
// register every name twice with Nuxt's auto-import and warn on each build).
// They stay reachable through this composable's returned object.
import {RecurrenceMode, RecurrenceUnit, MonthlyPattern, recurrenceRuleText} from '~/composables/useMeetAdmin';
// What cancelling THIS booking would cost (release fixes, 2 Oct 2026). A late
// cancellation is charged under the tiers and fee the member agreed when they
// booked, which the API sends as occurrence.myCancellation for the viewer's own
// booking on a paid date. The meet's CURRENT tiers and price are only the
// fallback for an older API. Returns a meet-shaped object, so tierRows,
// activeTier and freeCancellationUntil take it unchanged. Mirrored by the app's
// meet_format.dart.
export const cancellationTermsFor = (meet, occurrence) => {
  const mine = occurrence?.myCancellation;
  if (!mine) return meet;
  return {
    ...meet,
    feeMinor: mine.feeMinor ?? meet?.feeMinor,
    currency: mine.currency ?? meet?.currency,
    cancellationTiers: mine.tiers ?? [],
  };
};

// The dates leaving the meet would cancel, for the leave dialog. Each row's
// occurrence is the DETAIL (it carries myCancellation, the booking's agreed
// tiers and fee) laid over the list row, so the dialog prices a date the way
// the server's Leave charges it. An older API detail without myCancellation
// falls back to the meet's terms in cancellationTermsFor.
export const myUpcomingBookings = (upcoming, details) => (upcoming ?? [])
    .map(o => {
      const detail = details?.[o.id];
      return {occurrence: detail ? {...o, ...detail} : o, myAttendance: detail?.myAttendance ?? null};
    })
    .filter(x => x.myAttendance);

// Upcoming dates whose detail is not loaded yet (fetched before the leave
// dialog opens, so no booking is missed or priced from the list row).
export const occurrencesMissingDetail = (upcoming, details) => (upcoming ?? [])
    .filter(o => !details?.[o.id])
    .map(o => o.id);

// MeetBillingFrequency (Meet.cs)
export const BillingFrequency = {PerOccurrence: 0, Weekly: 1, Monthly: 2};
// MeetOccurrenceStatus (MeetOccurrence.cs)
export const OccurrenceStatus = {Scheduled: 0, Cancelled: 1, Completed: 2, Finalised: 3};
// MeetOfferReason (MeetAttendance.cs) — why a row is PromotionOffered
export const OfferReason = {WaitlistPromotion: 0, AdminAdd: 1, PriceChange: 2, Rescheduled: 3, TermsChange: 4};
// MeetAttendanceStatus (MeetAttendance.cs)
export const AttendanceStatus = {
  Attending: 0,
  Declined: 1,
  Waitlisted: 2,
  PromotionOffered: 3,
  CancelledFree: 4,
  CancelledPendingCharge: 5,
  CancelledCharged: 6,
  CancelledRefilled: 7,
  NoShow: 8,
  Attended: 9,
};

// Fee-bearer=Attendee gross-up — mirrors ChargePipelineService.ComputeAmounts:
//   gross = (net + 20p) / (1 − 1.5% − commission%)
// The platform default commission (1%) is duplicated here for DISPLAY only;
// the server snapshot is authoritative at charge time.

export function useMeetFormat() {
  const {money} = usePaymentsDisplay();

  const local = (dateStr) =>
      DateTime.fromISO(dateStr, {zone: 'utc'}).setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // "Wed 9 Jul"
  const occurrenceDay = (occurrence) => local(occurrence.startsAt).toFormat('ccc d LLL');
  // "Wednesday 9 July · 19:00 – 21:00"
  const occurrenceWhen = (occurrence) =>
      `${local(occurrence.startsAt).toFormat('cccc d LLLL')} · ${occurrenceTime(occurrence)}`;
  // "19:00 – 21:00", or just "19:00" when the meet has no fixed length (endsAt null)
  const occurrenceTime = (occurrence) => {
    const start = local(occurrence.startsAt).toFormat('HH:mm');
    return occurrence.endsAt ? `${start} to ${local(occurrence.endsAt).toFormat('HH:mm')}` : start;
  };

  // "Starts in 3 days" / "Started 2 hours ago"
  const startsInText = (occurrence) => {
    const start = local(occurrence.startsAt);
    const now = DateTime.now();
    const diff = start.diff(now, ['days', 'hours', 'minutes']);
    if (diff.toMillis() <= 0) {
      // No fixed length (endsAt null) → treat the start as the reference point.
      const ended = local(occurrence.endsAt ?? occurrence.startsAt) < now;
      return ended ? 'This date has passed' : 'Happening now';
    }
    if (diff.days >= 1) return `Starts in ${Math.round(diff.days)} day${Math.round(diff.days) === 1 ? '' : 's'}`;
    if (diff.hours >= 1) return `Starts in ${Math.round(diff.hours)} hour${Math.round(diff.hours) === 1 ? '' : 's'}`;
    return `Starts in ${Math.max(1, Math.round(diff.minutes))} min`;
  };

  // "every Wednesday" / "every other Thursday" / "every 3 weeks on Friday" /
  // "monthly on the last Friday" / "one-off" / "ad-hoc dates"
  const recurrenceText = (meet) => {
    if (meet.recurrenceMode === RecurrenceMode.OneOff) return 'one-off';
    if (meet.recurrenceMode === RecurrenceMode.ManualRecurring) return 'ad-hoc dates';
    if (!meet.firstOccursAt) return 'recurring';
    const rule = recurrenceRuleText(local(meet.firstOccursAt), meet.recurrenceUnit, meet.recurrenceInterval, meet.monthlyPattern);
    return rule.charAt(0).toLowerCase() + rule.slice(1);
  };

  // "Wednesdays 19:00 – 21:00" (facts chip on the hub)
  const scheduleChipText = (meet) => {
    const start = local(meet.firstOccursAt);
    const end = start.plus({minutes: meet.durationMinutes});
    const times = `${start.toFormat('HH:mm')} to ${end.toFormat('HH:mm')}`;
    if (meet.recurrenceMode === RecurrenceMode.OneOff) return `${start.toFormat('ccc d LLL')} · ${times}`;
    if (meet.recurrenceMode === RecurrenceMode.ManualRecurring) return `Ad-hoc dates · ${times}`;
    if (meet.recurrenceUnit === RecurrenceUnit.Week && (meet.recurrenceInterval ?? 1) === 1) {
      return `${start.toFormat('cccc')}s ${times}`;
    }
    return `${recurrenceRuleText(start, meet.recurrenceUnit, meet.recurrenceInterval, meet.monthlyPattern)} · ${times}`;
  };

  const isPaid = (meet) => meet?.feeMinor != null;

  // "£6.50" (face price)
  const feeText = (meet) => money(meet.feeMinor, meet.currency);

  // The price THIS date bills at. A price change never rewrites a date already
  // inside its notice window, so a date can differ from the meet's current fee;
  // every money line about one date uses this, never meet.feeMinor. Falls back
  // to the meet's fee for older responses.
  const dateFeeMinor = (meet, occurrence) => occurrence?.meetFeeMinor ?? meet?.feeMinor;
  const dateFeeText = (meet, occurrence) => money(dateFeeMinor(meet, occurrence), meet.currency);


  // "charged after the event" / "added to your weekly/monthly bill"
  const chargeTimingText = (meet) => {
    switch (meet.billingFrequency) {
      case BillingFrequency.Weekly: return 'added to your weekly bill';
      case BillingFrequency.Monthly: return 'added to your monthly bill';
      default: return 'charged after the event';
    }
  };

  const rsvpClosesAt = (meet, occurrence) => {
    // Occurrence detail carries rsvpClosesAt; derive it for list rows.
    if (occurrence.rsvpClosesAt) return local(occurrence.rsvpClosesAt);
    return local(occurrence.startsAt).minus({minutes: meet?.rsvpCloseMinutesBeforeStart ?? 0});
  };
  const rsvpClosed = (meet, occurrence) => rsvpClosesAt(meet, occurrence) <= DateTime.now();

  // The RSVP-open gate: server says whether the moment has passed. Random-mode
  // meets keep the exact moment secret (rsvpOpensAt comes back null until open).
  const rsvpNotYetOpen = (occurrence) => occurrence.rsvpIsOpen === false;
  const rsvpOpensText = (occurrence) => occurrence.rsvpOpensAt
      ? `RSVPs open ${local(occurrence.rsvpOpensAt).toFormat('ccc d LLL, HH:mm')}`
      : 'RSVPs open at a surprise moment before the event';
  // "in 3 days" / "in 2 hours" — the countdown that makes the gate obvious.
  const rsvpOpensInText = (occurrence) => {
    if (!occurrence.rsvpOpensAt) return null;
    const opens = local(occurrence.rsvpOpensAt);
    return opens > DateTime.now() ? opens.toRelative() : null;
  };

  const occurrencePast = (occurrence) => local(occurrence.startsAt) <= DateTime.now();
  // Ended ≠ past: RSVPs close at the start, but finalisation surfaces wait for
  // the session to finish (falling back to the start when there's no end time).
  const occurrenceEnded = (occurrence) => local(occurrence.endsAt ?? occurrence.startsAt) <= DateTime.now();
  const occurrenceCancelled = (occurrence) => occurrence.status === OccurrenceStatus.Cancelled;

  // The cap that applies to a date: its own override (occurrence.maxAttendees is
  // the effective value from the API), else the meet's default; null = unlimited.
  const maxAttendees = (meet, occurrence) =>
      occurrence?.maxAttendees !== undefined ? occurrence.maxAttendees : (meet?.maxAttendees ?? null);

  const isFull = (meet, occurrence) => {
    const max = maxAttendees(meet, occurrence);
    return max != null && occurrence.attendingCount >= max;
  };

  const spotsLeft = (meet, occurrence) => {
    const max = maxAttendees(meet, occurrence);
    return max != null ? Math.max(0, max - occurrence.attendingCount) : null;
  };

  const capacityPercent = (meet, occurrence) => {
    const max = maxAttendees(meet, occurrence);
    return max ? Math.min(100, Math.round((occurrence.attendingCount / max) * 100)) : 0;
  };

  // "11 of 14 going · 3 spots left" / "14 of 14 going · full · waitlist of 3" / "11 going"
  const capacityText = (meet, occurrence) => {
    const max = maxAttendees(meet, occurrence);
    if (max == null) {
      return `${occurrence.attendingCount} going`;
    }
    if (isFull(meet, occurrence)) {
      const wl = occurrence.waitlistedCount > 0 ? ` · waitlist of ${occurrence.waitlistedCount}` : '';
      return `${occurrence.attendingCount} of ${max} going · full${wl}`;
    }
    const left = spotsLeft(meet, occurrence);
    return `${occurrence.attendingCount} of ${max} going · ${left} spot${left === 1 ? '' : 's'} left`;
  };

  // Terms are stored as termsHtml but the seeded template (and textarea-edited
  // terms) are PLAIN TEXT — rendering that with v-html collapses the newlines
  // into one run-on paragraph. Tag-free text is escaped and gets its line
  // breaks back; anything with real markup is reduced to a formatting
  // allow-list first, because a club admin wrote it and members read it
  // (stored XSS; mirrors the API's EmailTermsFormat.Sanitise).
  // Every v-html of club terms goes through here: entry fee terms too.
  const termsAsHtml = (terms) => {
    if (!terms) return '';
    if (looksLikeHtml(terms)) return sanitiseTermsHtml(terms);
    return terms
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
  };

  // The one line under an offer's title (owner, 26 Sep 2026: keep the card
  // short): the price, then when the offer runs out.
  // "£5.00 · offer expires Wed 30 Sep, 17:00"; a price change adds the old
  // price ("£6.00 (was £5.00) · ..."); a passed offer says "offer ended".
  // The payee and the no-show line live in the accept dialog, where the member
  // agrees. Mirrored by the app's meet_format.dart offerLine.
  const offerLine = ({feeMinor, currency, showPrevious = false, previousFeeMinor, expiresAt}, now = DateTime.now()) => {
    let price = feeMinor != null ? money(feeMinor, currency) : 'Free';
    if (showPrevious) price += ` (was ${previousFeeMinor != null ? money(previousFeeMinor, currency) : 'free'})`;
    if (!expiresAt) return price;
    const at = local(expiresAt);
    return `${price} · offer ${at < now ? 'ended' : 'expires'} ${at.toFormat('ccc d LLL, HH:mm')}`;
  };

  // Whether an offer commits the member to anything worth listing: a fee, a
  // cancellation policy or club terms. A free date with none of them shows no
  // "If you accept" box at all rather than an empty one.
  const offerCommitsToSomething = (summary) =>
    summary?.feeMinor != null || (summary?.cancellationTiers?.length ?? 0) > 0 || !!summary?.termsHtml;

  // ── Cancellation tiers (MEETS_PLAN §4.4) ──────────────────────────────────
  // Sorted furthest-out first (as the detail response is). Rows for the tier
  // table, matching the admin editor's band wording: "48h+ before: Free ·
  // Between 24 and 48h before: 50% · Final 24h: 80% · No-show: 100%".
  const tierRows = (meet, occurrence) => {
    const tiers = [...(meet?.cancellationTiers ?? [])].sort((a, b) => b.hoursBefore - a.hoursBefore);
    if (!isPaid(meet) || tiers.length === 0) return [];
    const hoursUntil = occurrence
        ? local(occurrence.startsAt).diff(DateTime.now(), 'hours').hours
        : Infinity;

    const rows = [{
      label: `${tiers[0].hoursBefore}h+ before the start`,
      percent: 0,
      active: hoursUntil > tiers[0].hoursBefore,
    }];
    tiers.forEach((tier, i) => {
      const next = tiers[i + 1];
      rows.push({
        label: next ? `Between ${next.hoursBefore} and ${tier.hoursBefore}h before` : `Final ${tier.hoursBefore}h`,
        percent: tier.chargePercent,
        active: hoursUntil <= tier.hoursBefore && (!next || hoursUntil > next.hoursBefore),
      });
    });
    // A no-show is charged in full whatever the last tier says
    // (MeetFinalisationService). Its own row, never the "cancel now" one.
    // Mirrored by the app's meet_format.dart.
    rows.push({label: 'No-show', percent: 100, active: false});
    return rows;
  };

  // The tier that applies if the user cancels NOW (null = free).
  const activeTier = (meet, occurrence) => {
    const row = tierRows(meet, occurrence).find(r => r.active);
    return row && row.percent > 0 ? row : null;
  };

  // "Tue 8 July, 19:00" — the last free-cancellation moment (start − outermost
  // tier). Null once it has passed: a late booking is told what cancelling now
  // costs, and a free-cancellation time already gone beside that warning says
  // the opposite. Mirrored by the app's meet_format.dart.
  const freeCancellationUntil = (meet, occurrence, now = DateTime.now()) => {
    const tiers = [...(meet?.cancellationTiers ?? [])].sort((a, b) => b.hoursBefore - a.hoursBefore);
    if (!isPaid(meet) || tiers.length === 0) return null;
    const until = local(occurrence.startsAt).minus({hours: tiers[0].hoursBefore});
    if (until <= now) return null;
    return until.toFormat('ccc d LLLL, HH:mm');
  };

  // "then 50%, rising to 100% within 2h of start" (facts-line summary)
  const tierSummaryText = (meet) => {
    const tiers = [...(meet?.cancellationTiers ?? [])].sort((a, b) => b.hoursBefore - a.hoursBefore);
    if (tiers.length === 0) return null;
    if (tiers.length === 1) return `then ${tiers[0].chargePercent}%`;
    const last = tiers[tiers.length - 1];
    return `then ${tiers[0].chargePercent}%, rising to ${last.chargePercent}% within ${last.hoursBefore}h of start`;
  };

  // Status chip for MY attendance (occurrence cards + event page)
  const attendanceChip = (attendance) => {
    if (!attendance) return {label: 'Not responded', chipClass: '', icon: 'mdi-circle-outline'};
    switch (attendance.status) {
      case AttendanceStatus.Attending:
        return {label: 'Attending', chipClass: 'tl-chip--ok', icon: 'mdi-check-circle'};
      case AttendanceStatus.Declined:
        return {label: 'Not going', chipClass: '', icon: 'mdi-close'};
      case AttendanceStatus.Waitlisted:
        return {
          label: attendance.waitlistPosition ? `Waitlisted · #${attendance.waitlistPosition}` : 'Waitlisted',
          chipClass: 'tl-chip--warn', icon: 'mdi-timer-sand',
        };
      case AttendanceStatus.PromotionOffered:
        if (attendance.offerReason === OfferReason.PriceChange) {
          return {label: 'Confirm new price', chipClass: 'tl-chip--warn', icon: 'mdi-cash-sync'};
        }
        if (attendance.offerReason === OfferReason.TermsChange) {
          return {label: 'Confirm new terms', chipClass: 'tl-chip--warn', icon: 'mdi-file-document-edit-outline'};
        }
        if (attendance.offerReason === OfferReason.Rescheduled) {
          return {label: 'Confirm new time', chipClass: 'tl-chip--warn', icon: 'mdi-calendar-clock'};
        }
        if (attendance.offerReason === OfferReason.AdminAdd) {
          return {label: 'Spot held for you', chipClass: 'tl-chip--warn', icon: 'mdi-hand-back-right-outline'};
        }
        return {label: 'Spot offered', chipClass: 'tl-chip--warn', icon: 'mdi-party-popper'};
      case AttendanceStatus.CancelledFree:
        return {label: 'Cancelled', chipClass: '', icon: 'mdi-calendar-remove'};
      case AttendanceStatus.CancelledPendingCharge:
        return {label: 'Cancelled late', chipClass: 'tl-chip--warn', icon: 'mdi-calendar-remove'};
      case AttendanceStatus.CancelledCharged:
        return {label: 'Cancelled · charged', chipClass: 'tl-chip--warn', icon: 'mdi-calendar-remove'};
      case AttendanceStatus.CancelledRefilled:
        return {label: 'Spot filled · no charge', chipClass: 'tl-chip--ok', icon: 'mdi-swap-horizontal'};
      case AttendanceStatus.NoShow:
        return {label: 'No-show', chipClass: 'tl-chip--warn', icon: 'mdi-account-off'};
      case AttendanceStatus.Attended:
        return {label: 'Attended', chipClass: 'tl-chip--ok', icon: 'mdi-check-circle'};
      default:
        return {label: 'Unknown', chipClass: '', icon: null};
    }
  };

  // Register groupings for the rails
  const goingRows = (attendances) =>
      (attendances ?? []).filter(a => a.status === AttendanceStatus.Attending || a.status === AttendanceStatus.Attended);
  const waitlistRows = (attendances) =>
      (attendances ?? []).filter(a => a.status === AttendanceStatus.Waitlisted || a.status === AttendanceStatus.PromotionOffered);
  const declinedRows = (attendances) =>
      (attendances ?? []).filter(a => [
        AttendanceStatus.Declined,
        AttendanceStatus.CancelledFree,
        AttendanceStatus.CancelledPendingCharge,
        AttendanceStatus.CancelledCharged,
        AttendanceStatus.CancelledRefilled,
      ].includes(a.status));

  return {
    termsAsHtml,
    RecurrenceMode,
    RecurrenceUnit,
    MonthlyPattern,
    BillingFrequency,
    OccurrenceStatus,
    AttendanceStatus,
    money,
    occurrenceDay,
    occurrenceWhen,
    occurrenceTime,
    startsInText,
    recurrenceText,
    scheduleChipText,
    isPaid,
    feeText,
    dateFeeMinor,
    dateFeeText,
    chargeTimingText,
    rsvpClosesAt,
    rsvpClosed, rsvpNotYetOpen, rsvpOpensText, rsvpOpensInText,
    occurrencePast,
    occurrenceEnded,
    occurrenceCancelled,
    isFull,
    maxAttendees,
    spotsLeft,
    capacityPercent,
    capacityText,
    tierRows,
    activeTier,
    cancellationTermsFor,
    offerLine,
    offerCommitsToSomething,
    freeCancellationUntil,
    tierSummaryText,
    attendanceChip,
    goingRows,
    waitlistRows,
    declinedRows,
  };
}
